/**
 * Modal component that prompts users to install or update the Chrome extension.
 *
 * Shows when the extension is not detected or needs updating.
 * Reusable across any view that needs extension capabilities.
 *
 * Usage:
 * ```tsx
 * import { ExtensionRequiredModal, useExtensionStatus } from '../services/extension';
 *
 * function MyView() {
 *   const { connectionStatus, updateInfo } = useExtensionStatus();
 *   const [dismissed, setDismissed] = useState(false);
 *
 *   // Show for not_installed OR connection_failed (Chrome API unavailable)
 *   const needsInstall = connectionStatus === 'not_installed' || connectionStatus === 'connection_failed';
 *   const needsUpdate = connectionStatus === 'connected' && updateInfo?.status === 'required';
 *
 *   return (
 *     <>
 *       <ExtensionRequiredModal
 *         isOpen={(needsInstall || needsUpdate) && !dismissed}
 *         onClose={() => setDismissed(true)}
 *         downloadUrl={updateInfo?.downloadUrl}
 *         mode={needsUpdate ? 'update' : 'install'}
 *       />
 *       {// ... rest of your view}
 *     </>
 *   );
 * }
 * ```
 */

import { useCallback, useState, useRef, useEffect } from "react";
import { Modal } from "../../shared-components/Modal";
import Button, { ButtonColorTypes } from "../../shared-components/Button";
import {
  startDownload,
  getDownloadErrorType,
  getDownloadErrorMessage,
} from "./downloadUtils";

// Puzzle piece icon for extension
const ExtensionIcon = () => (
  <svg
    className="w-6 h-6 text-ourGreen"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z"
    />
  </svg>
);

export type ExtensionModalMode =
  | "install"
  | "required_update"
  | "optional_update";

export interface ExtensionRequiredModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Called when the modal is closed (dismiss or after download) */
  onClose: () => void;
  /** Download URL for the extension (from updateInfo.downloadUrl) */
  downloadUrl?: string;
  /** Mode: 'install' for first-time install, 'required_update' for mandatory update, 'optional_update' for suggested update */
  mode?: ExtensionModalMode;
  /** Optional custom title (overrides mode-based title) */
  title?: string;
  /** Optional custom message (overrides mode-based message) */
  message?: string;
  /** Optional: function to re-check extension status after installation */
  onCheckAgain?: () => Promise<boolean>;
}

const DEFAULT_TITLES: Record<ExtensionModalMode, string> = {
  install: "Chrome Extension Required",
  required_update: "Extension Update Required",
  optional_update: "Extension Update Available",
};

const DEFAULT_MESSAGES: Record<ExtensionModalMode, string> = {
  install:
    "To scrape external content, you'll need to install the Salvage Yard Chrome extension. Download it below and follow the installation instructions.",
  required_update:
    "A newer version of the Salvage Yard extension is required to continue. Please download and install the update.",
  optional_update:
    "A new version of the Salvage Yard extension is available. We recommend updating for the best experience and latest features.",
};

const BUTTON_LABELS: Record<ExtensionModalMode, string> = {
  install: "Download Extension",
  required_update: "Download Update",
  optional_update: "Download Update",
};

export function ExtensionRequiredModal({
  isOpen,
  onClose,
  downloadUrl,
  mode = "install",
  title,
  message,
  onCheckAgain,
}: ExtensionRequiredModalProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [checkResult, setCheckResult] = useState<
    "success" | "not_found" | null
  >(null);
  const [urlCopied, setUrlCopied] = useState(false);
  // Track if we're in the "success closing" phase to prevent early close
  const [isClosing, setIsClosing] = useState(false);

  // Track timeouts for cleanup
  const checkAgainTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const urlCopiedTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeouts on unmount or when modal closes
  useEffect(() => {
    return () => {
      if (checkAgainTimeoutRef.current) {
        clearTimeout(checkAgainTimeoutRef.current);
        checkAgainTimeoutRef.current = null;
      }
      if (urlCopiedTimeoutRef.current) {
        clearTimeout(urlCopiedTimeoutRef.current);
        urlCopiedTimeoutRef.current = null;
      }
    };
  }, []);

  // Clear timeout when modal closes externally
  useEffect(() => {
    if (!isOpen && !isClosing) {
      if (checkAgainTimeoutRef.current) {
        clearTimeout(checkAgainTimeoutRef.current);
        checkAgainTimeoutRef.current = null;
      }
    }
  }, [isOpen, isClosing]);

  // Reset state when modal reopens to prevent stale state from previous opens
  useEffect(() => {
    if (isOpen) {
      setDownloadError(null);
      setCheckResult(null);
      setUrlCopied(false);
      setIsClosing(false);
      setIsDownloading(false);
      setIsChecking(false);
    }
  }, [isOpen]);

  const handleCopyExtensionsUrl = useCallback(async () => {
    // Clear any existing timeout
    if (urlCopiedTimeoutRef.current) {
      clearTimeout(urlCopiedTimeoutRef.current);
    }

    try {
      await window.navigator.clipboard.writeText("chrome://extensions");
      // Only show success if clipboard write succeeded
      setUrlCopied(true);
      urlCopiedTimeoutRef.current = setTimeout(() => {
        setUrlCopied(false);
        urlCopiedTimeoutRef.current = null;
      }, 2000);
    } catch (error) {
      // Clipboard write failed (e.g., permissions denied)
      // Don't show success state - user will see no feedback on failure
      console.warn(
        "[ExtensionRequiredModal] Failed to copy to clipboard:",
        error
      );
    }
  }, []);

  const displayTitle = title || DEFAULT_TITLES[mode];
  const displayMessage = message || DEFAULT_MESSAGES[mode];
  const buttonLabel = BUTTON_LABELS[mode];

  const handleDownload = useCallback(async () => {
    if (!downloadUrl) {
      setDownloadError("Download URL not available. Please try again later.");
      return;
    }

    setIsDownloading(true);
    setDownloadError(null);
    setCheckResult(null);

    try {
      await startDownload(downloadUrl, "salvage-yard-extension.zip");
      // Don't close automatically - user needs to install manually
    } catch (error) {
      const errorType = getDownloadErrorType(error);
      const errorMessage = getDownloadErrorMessage(error);
      console.error("[ExtensionRequiredModal] Download failed:", {
        errorType,
        error,
      });
      setDownloadError(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  }, [downloadUrl]);

  const handleCheckAgain = useCallback(async () => {
    if (!onCheckAgain) return;

    // Clear any existing timeout
    if (checkAgainTimeoutRef.current) {
      clearTimeout(checkAgainTimeoutRef.current);
      checkAgainTimeoutRef.current = null;
    }

    setIsChecking(true);
    setCheckResult(null);
    setDownloadError(null);

    try {
      const found = await onCheckAgain();
      if (found) {
        setCheckResult("success");
        setIsClosing(true); // Prevent external isOpen changes from closing early
        // Auto-close after showing success message
        checkAgainTimeoutRef.current = setTimeout(() => {
          setIsClosing(false);
          onClose();
          checkAgainTimeoutRef.current = null;
        }, 3000);
      } else {
        setCheckResult("not_found");
      }
    } catch (error) {
      console.error("[ExtensionRequiredModal] Check failed:", error);
      setCheckResult("not_found");
    } finally {
      setIsChecking(false);
    }
  }, [onCheckAgain, onClose]);

  // Keep modal open during success message even if external isOpen becomes false
  const effectiveIsOpen = isOpen || isClosing;

  return (
    <Modal
      open={effectiveIsOpen}
      onClose={onClose}
      title={displayTitle}
      width="w-full max-w-lg"
      icon={<ExtensionIcon />}
    >
      <div className="mt-4">
        <p className="text-sm text-gray-600">{displayMessage}</p>

        {/* Installation Instructions */}
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 mb-2">
            {mode === "install" ? "Installation Steps:" : "Update Steps:"}
          </h4>
          <ol className="text-sm text-gray-600 list-decimal list-inside space-y-1">
            <li>Click "{buttonLabel}" below</li>
            <li>Extract the downloaded ZIP file</li>
            <li>
              Open Chrome and go to{" "}
              <button
                type="button"
                onClick={handleCopyExtensionsUrl}
                className="inline-flex items-center gap-1 bg-gray-200 px-1.5 py-0.5 rounded text-xs font-mono hover:bg-gray-300 cursor-pointer transition-colors relative"
                title="Click to copy"
              >
                chrome://extensions
                <svg
                  className="w-3 h-3 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                {urlCopied && (
                  <span className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                    Copied!
                  </span>
                )}
              </button>
            </li>
            {(mode === "required_update" || mode === "optional_update") && (
              <li>
                Find "Salvage Yard" and click the refresh icon, or remove and
                re-add
              </li>
            )}
            {mode === "install" && (
              <>
                <li>Enable "Developer mode" (top right toggle)</li>
                <li>Click "Load unpacked" and select the extracted folder</li>
              </>
            )}
          </ol>
        </div>

        {/* Error Message */}
        {downloadError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{downloadError}</p>
          </div>
        )}

        {/* Check Result Messages */}
        {checkResult === "success" && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-700">
              ✓ Extension detected! Closing...
            </p>
          </div>
        )}
        {checkResult === "not_found" && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-700">
              Extension not detected yet. Make sure you've loaded it in
              chrome://extensions and refresh.
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-3">
          {onCheckAgain && (
            <Button
              text={isChecking ? "Checking..." : "Check Again"}
              colorType={ButtonColorTypes.cancel} // cancel = white bg, not red
              onClick={handleCheckAgain}
              disabled={isChecking || isDownloading}
            />
          )}
          <Button
            text={isDownloading ? "Downloading..." : buttonLabel}
            colorType={ButtonColorTypes.primary}
            onClick={handleDownload}
            disabled={isDownloading || !downloadUrl}
          />
        </div>
      </div>
    </Modal>
  );
}

export default ExtensionRequiredModal;
